import React, { useEffect } from 'react';
import { tx, tr } from '../../utils/i18n';
import { routePath } from '../../i18n/routes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, Search, Users, TrendingUp, Shield, Target, Zap } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import Header from '../Header';
import Footer from '../Footer';
import { Toaster } from 'sonner';
import SEOHead from '../SEOHead';

const AutocompleteService = () => {
  const { t, currentLanguage } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Browser Autocomplete Advertising",
    "description": "Publicité dans l'autocomplétion des navigateurs. Intent temps réel, ROI 12x vs display, taux d'engagement 75%.",
    "provider": {
      "@type": "Organization",
      "name": "OptimHub"
    }
  };

  const features = [
    {
      icon: Search,
      title: tx(currentLanguage, 'Intent en Temps Réel', 'Real-time Intent', 'Echtzeit-Intent'),
      description: tx(currentLanguage, 'Captez l\'intention d\'achat au moment exact de la recherche', 'Capture purchase intent at the exact moment of search', 'Erfassen Sie die Kaufabsicht zum exakten Zeitpunkt der Suche')
    },
    {
      icon: Users,
      title: tx(currentLanguage, 'Première Impression', 'First Impression', 'Erster Eindruck'),
      description: tx(currentLanguage, 'Soyez la première marque visible avant même que l\'utilisateur ne termine sa recherche', 'Be the first brand visible before the user even finishes their search', 'Seien Sie die erste sichtbare Marke, bevor der Nutzer seine Suche beendet')
    },
    {
      icon: Shield,
      title: tx(currentLanguage, 'Taux de Conversion Élevé', 'High Conversion Rate', 'Hohe Conversion-Rate'),
      description: tx(currentLanguage, 'Conversion optimisée grâce à l\'intention immédiate des utilisateurs', 'Optimized conversion thanks to immediate user intent', 'Optimierte Conversion dank unmittelbarer Nutzerintention')
    }
  ];

  const benefits = [
    {
      icon: Target,
      title: tx(currentLanguage, 'Ciblage Ultra-Précis', 'Ultra-Precise Targeting', 'Ultra-präzise Ausrichtung'),
      description: tx(currentLanguage, 'Ciblage basé sur les termes de recherche exacts en temps réel', 'Targeting based on exact search terms in real time', 'Ausrichtung basierend auf exakten Suchbegriffen in Echtzeit')
    },
    {
      icon: TrendingUp,
      title: tx(currentLanguage, 'ROI Immédiat', 'Immediate ROI', 'Sofortiger ROI'),
      description: tx(currentLanguage, 'Résultats visibles dès les premières heures de campagne', 'Visible results from the first hours of campaign', 'Sichtbare Ergebnisse ab den ersten Kampagnenstunden')
    },
    {
      icon: Zap,
      title: tx(currentLanguage, 'Moment Optimal', 'Optimal Moment', 'Optimaler Moment'),
      description: tx(currentLanguage, 'Intervention au moment de plus haute intention d\'achat', 'Intervention at the moment of highest purchase intent', 'Intervention zum Zeitpunkt der höchsten Kaufabsicht')
    }
  ];

  const stats = [
    { value: '0.3s', label: tx(currentLanguage, 'Temps de Réaction', 'Response Time', 'Reaktionszeit') },
    { value: '75%', label: tx(currentLanguage, 'Taux d\'Engagement', 'Engagement Rate', 'Engagement-Rate') },
    { value: '12x', label: tx(currentLanguage, 'ROI vs Display', 'ROI vs Display', 'ROI vs Display') }
  ];

  const searchFlow = [
    {
      step: 1,
      query: 'iPhone 15',
      suggestion: tx(currentLanguage, 'iPhone 15 Pro Max', 'iPhone 15 Pro Max', 'iPhone 15 Pro Max'),
      action: tx(currentLanguage, 'Votre marque apparaît', 'Your brand appears', 'Ihre Marke erscheint')
    },
    {
      step: 2,
      query: 'Nike Air',
      suggestion: tx(currentLanguage, 'Nike Air Jordan', 'Nike Air Jordan', 'Nike Air Jordan'),
      action: tx(currentLanguage, 'Redirection immédiate', 'Immediate redirect', 'Sofortige Weiterleitung')
    },
    {
      step: 3,
      query: 'Samsung',
      suggestion: tx(currentLanguage, 'Samsung Galaxy S24', 'Samsung Galaxy S24', 'Samsung Galaxy S24'),
      action: tx(currentLanguage, 'Conversion optimisée', 'Optimized conversion', 'Optimierte Conversion')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={tr(currentLanguage, "Browser Autocomplete Advertising | Search Intent Real-Time Ads - OptimHub")}
        description={tr(currentLanguage, "Publicité dans autocomplétion navigateurs. Captez l'intent en temps réel, ROI 12x vs display, 75% taux d'engagement. Content commerce optimisé pour la recherche.")}
        keywords="browser autocomplete ads, search autocomplete advertising, real-time intent advertising, predictive search ads, browser suggestion ads, search intent marketing, content commerce search"
        ogImage="https://images.pexels.com/photos/1999463/pexels-photo-1999463.jpeg?w=1200"
        routeKey="serviceAutocomplete"
        lang={currentLanguage}
        schemaData={schemaData}
      />
      <Header />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
                {tx(currentLanguage, 'Recherche Prédictive', 'Predictive Search', 'Prädiktive Suche')}
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                Browser Autocomplete
                <span className="block text-blue-600 mt-2">
                  {tx(currentLanguage, 'Advertising', 'Advertising', 'Werbung')}
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {tx(currentLanguage, 'Interceptez l\'intention d\'achat en temps réel grâce à la technologie d\'autocomplétion des navigateurs. Soyez présent au moment où vos clients potentiels formulent leur recherche. En tant que traffic brokers, nous achetons du trafic au CPC et vous ne payez qu\'au CPA.', 'Intercept purchase intent in real time through browser autocomplete technology. Be present when your potential customers are formulating their search. As traffic brokers, we buy traffic at CPC and you only pay at CPA.', 'Fangen Sie Kaufabsichten in Echtzeit durch Browser-Autovervollständigungstechnologie ab. Seien Sie präsent, wenn Ihre potenziellen Kunden ihre Suche formulieren. Als Traffic-Broker kaufen wir Traffic zu CPC und Sie zahlen nur zu CPA.')
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Commencer maintenant', 'Get Started', 'Jetzt starten')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <a 
                  href="mailto:contact@optimhub.com?subject=Demande de démo Browser Autocomplete"
                  className="border-2 border-blue-200 text-blue-700 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 inline-flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Voir la démo', 'Watch Demo', 'Demo ansehen')}
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/1999463/pexels-photo-1999463.jpeg"
                alt="Browser Autocomplete Advertising"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Search Flow Demo */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Comment Ça Fonctionne', 'How It Works', 'Wie es funktioniert')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {tx(currentLanguage, 'Votre marque apparaît automatiquement dans les suggestions de recherche au moment optimal.', 'Your brand appears automatically in search suggestions at the optimal moment.', 'Ihre Marke erscheint automatisch in Suchvorschlägen zum optimalen Zeitpunkt.')
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {searchFlow.map((flow, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                  {flow.step}
                </div>
                <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
                  <div className="text-sm text-gray-500 mb-2">
                    {tx(currentLanguage, 'Utilisateur tape:', 'User types:', 'Nutzer tippt:')}
                  </div>
                  <div className="font-mono text-lg text-gray-900">{flow.query}</div>
                  <div className="text-sm text-blue-600 mt-2">→ {flow.suggestion}</div>
                </div>
                <div className="text-blue-600 font-semibold">{flow.action}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Technologie Autocomplete', 'Autocomplete Technology', 'Autovervollständigungs-Technologie')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-blue-50/30">
                  <CardHeader className="text-center pb-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed text-center">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Pourquoi Autocomplete ?', 'Why Autocomplete?', 'Warum Autovervollständigung?')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            {tx(currentLanguage, 'Prêt à Capturer l\'Intent ?', 'Ready to Capture Intent?', 'Bereit, Intent zu erfassen?')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            {tx(currentLanguage, 'Soyez présent au moment exact où vos clients recherchent vos produits.', 'Be present at the exact moment your customers search for your products.', 'Seien Sie genau dann präsent, wenn Ihre Kunden nach Ihren Produkten suchen.')
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
            >
              {tx(currentLanguage, 'Démarrer maintenant', 'Start Now', 'Jetzt starten')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <button 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors duration-200"
            >
              {tx(currentLanguage, 'Parler à un expert', 'Talk to an Expert', 'Mit Experten sprechen')}
            </button>
          </div>
        </div>
      </section>
      <Footer />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            border: '1px solid #e5e7eb',
          },
        }}
      />
    </div>
  );
};

export default AutocompleteService;